import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { tools } from "../../data/toolData";
import ToolCard from "../ui/ToolCard";
import { sectionBadge, fadeUp } from "../../utils/animationUtils";

gsap.registerPlugin(ScrollTrigger);

const ToolsSection = () => {
    const containerRef = useRef(null);
    const ghostRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.set(['.tools-badge', '.tools-heading'], { opacity: 0 });

            sectionBadge('.tools-badge');
            fadeUp('.tools-heading');

            // Marquee Track 1 (Right to Left) — store ref for per-track speed control
            const tween1 = gsap.to('.marquee-track-1', {
                x: "-50%",
                duration: 20,
                ease: "none",
                repeat: -1
            });

            // Marquee Track 2 (Left to Right) — store ref
            const tween2 = gsap.fromTo('.marquee-track-2',
                { x: "-50%" },
                { x: "0%", duration: 25, ease: "none", repeat: -1 }
            );

            // Hover: slow down ONLY the marquee tweens
            const wrapper = containerRef.current?.querySelector('.marquee-wrapper');
            let isHovering = false;
            
            const slowDown = () => {
                isHovering = true;
                gsap.to([tween1, tween2], { timeScale: 0.15, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
            };
            const speedUp = () => {
                isHovering = false;
                gsap.to([tween1, tween2], { timeScale: 1, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
            };
            if (wrapper) {
                wrapper.addEventListener('mouseenter', slowDown);
                wrapper.addEventListener('mouseleave', speedUp);
            }

            // Marquee Scroll Velocity (kinetic feedback)
            ScrollTrigger.create({
                trigger: containerRef.current,
                start: "top bottom",
                end: "bottom top",
                onUpdate: (self) => {
                    if (isHovering) return;
                    const velocity = Math.abs(self.getVelocity());
                    // Convert velocity to timeScale multiplier (min 1, max 4)
                    const targetScale = 1 + (velocity / 500); 
                    const clampedScale = Math.min(targetScale, 4);
                    
                    gsap.to([tween1, tween2], {
                        timeScale: clampedScale,
                        duration: 0.15,
                        ease: 'none',
                        overwrite: 'auto',
                        onComplete: () => {
                            if (!isHovering) {
                                gsap.to([tween1, tween2], { timeScale: 1, duration: 0.8, ease: 'power2.out', overwrite: 'auto' });
                            }
                        }
                    });
                }
            });

            // Ghost text parallax: x:-80 scrub:1 per spec
            gsap.to(ghostRef.current, {
                x: -80,
                ease: 'none',
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1,
                }
            });

            // Tool card hover lift
            const toolCards = gsap.utils.toArray('.tool-card');
            toolCards.forEach(card => {
                card.addEventListener('mouseenter', () => gsap.to(card, { x: -3, y: -3, duration: 0.2, ease: 'power2.out' }));
                card.addEventListener('mouseleave', () => gsap.to(card, { x: 0, y: 0, ease: 'elastic.out(1,0.6)', duration: 0.4 }));
            });

            return () => {
                if (wrapper) {
                    wrapper.removeEventListener('mouseenter', slowDown);
                    wrapper.removeEventListener('mouseleave', speedUp);
                }
            };
        }, containerRef);

        return () => ctx.revert();
    }, []);

    const midIndex = Math.ceil(tools.length / 2);
    const row1Tools = tools.slice(0, midIndex);
    const row2Tools = tools.slice(midIndex);

    return (
        <section id="tools" ref={containerRef} className="py-24 font-jakarta relative overflow-hidden bg-[#F2EFE9]">
            {/* Ghost Watermark */}
            <span
                ref={ghostRef}
                className="select-none pointer-events-none font-black absolute top-1/2 left-1/2 z-0"
                style={{
                    fontSize: '20vw',
                    opacity: 0.04,
                    color: '#111',
                    transform: 'translate(-50%, -50%)',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    fontFamily: 'Space Mono, monospace',
                }}
            >
                STACK
            </span>

            <div className="max-w-7xl mx-auto px-6 relative z-10 mb-14">
                <div className="tools-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C1E] text-white border-2 border-[#111] rounded-full font-space font-bold text-sm shadow-brutal-sm mb-5">
                    <span className="w-2 h-2 rounded-full bg-[#AAFF00] inline-block"></span>
                    TECH STACK
                </div>
                <h2 className="tools-heading text-4xl md:text-5xl lg:text-6xl font-black text-[#111] leading-tight">
                    Core Technology <span className="text-[#555]">Stack.</span>
                </h2>
            </div>

            <div className="marquee-wrapper w-full overflow-hidden relative z-10">
                {/* Row 1 → Left */}
                <div className="flex mb-6">
                    <div className="marquee-track-1 flex gap-5">
                        {[...row1Tools, ...row1Tools, ...row1Tools, ...row1Tools].map((tool, idx) => (
                            <div key={idx} className="flex-shrink-0 w-[140px] sm:w-[168px]">
                                <ToolCard name={tool.name} IconComponent={tool.IconComponent} color={tool.color} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Row 2 → Right */}
                <div className="flex">
                    <div className="marquee-track-2 flex gap-5">
                        {[...row2Tools, ...row2Tools, ...row2Tools, ...row2Tools].map((tool, idx) => (
                            <div key={idx} className="flex-shrink-0 w-[140px] sm:w-[168px]">
                                <ToolCard name={tool.name} IconComponent={tool.IconComponent} color={tool.color} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ToolsSection;